import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Menu from "components/common/Menu";
import { CommonStyles } from "styles/CommonStyles";
import { SideBarTabs } from "constants/tabs";
import { useTabActive } from "hook/useTabActive";
import {
  IoAddOutline,
  IoArrowBack,
  IoArrowDown,
  IoArrowUp,
  IoCameraOutline,
  IoCheckmarkCircle,
  IoClose,
  IoCloudDoneOutline,
  IoCropOutline,
  IoImagesOutline,
  IoRefreshOutline,
  IoSearchOutline,
  IoTrashOutline,
} from "react-icons/io5";
import { getCarMakes, getDealers } from "api/services/dealer";
import {
  CaptureDraftPatch,
  createCaptureDraft,
  deleteCaptureDraft,
  deleteCaptureDraftImage,
  getCaptureDraft,
  getCaptureDrafts,
  publishCaptureDraft,
  quickCreateDealer,
  replaceCaptureDraftImage,
  retryCaptureTelegram,
  updateCaptureDraft,
  uploadCaptureDraftImage,
} from "api/services/captureDrafts";
import {
  CarCaptureDraftType,
  CarMakeType,
  CaptureDraftImageType,
  DealerType,
} from "types/loadedData";
import {
  getPendingCaptureUploads,
  putPendingCaptureUpload,
  removePendingCaptureUpload,
  removePendingCaptureUploadsForDraft,
  PendingCaptureUpload,
} from "utils/captureUploadQueue";
import { compressCaptureImage, cropCaptureImage } from "utils/captureImages";
import ContinuousCamera from "components/capture/ContinuousCamera";

type ViewMode = "capture" | "drafts" | "edit";
type LocalUpload = {
  key: string;
  draftId: string;
  preview: string;
  status: "uploading" | "failed";
  attempts: number;
  lastError: string;
};

const MAX_UPLOAD_ATTEMPTS = 3;
const RETRY_DELAYS = [1000, 3000, 8000];

const captureUploadError = (error: any) => ({
  status: Number(error?.response?.status || 0),
  message:
    error?.response?.data?.msg ||
    error?.response?.data?.error ||
    error?.message ||
    "Photo upload failed",
});

const CAR_COLORS = [
  "Black",
  "White",
  "Silver",
  "Gray",
  "Blue",
  "Red",
  "Green",
  "Brown",
  "Beige",
  "Gold",
  "Yellow",
  "Orange",
  "Smoky",
];

const newClientId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const formatPriceInput = (value: string | number | undefined) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits ? Number(digits).toLocaleString("en-US") : "";
};

const rawNumber = (value: string) => value.replace(/,/g, "");

const makeId = (make?: CarMakeType | string) =>
  typeof make === "string" ? make : make?._id || "";

const draftMissing = (draft: CarCaptureDraftType) => {
  const missing: string[] = [];
  if (!draft.price) missing.push("price");
  if (!draft.make) missing.push("brand");
  if (!draft.model) missing.push("model");
  if (!draft.year) missing.push("year");
  if (!draft.fuelType) missing.push("fuel");
  if (!draft.transmissionType) missing.push("transmission");
  if (draft.usageType === "new" && !draft.color) missing.push("color");
  const selected = draft.images?.filter((image) => image.selected).length || 0;
  if (selected < 3) missing.push("3 photos");
  return missing;
};

function QueuedUploadTile({
  upload,
  onRetry,
  onRemove,
}: {
  upload: LocalUpload;
  onRetry: (key: string) => void;
  onRemove: (key: string) => void;
}) {
  const failed = upload.status === "failed";
  return (
    <div
      className={`${ClassName.photoThumb} ${failed ? "ring-2 ring-primary" : "opacity-70"}`}
      title={upload.lastError || (failed ? "Upload failed" : "Uploading")}
    >
      <img src={upload.preview} alt={failed ? "Upload failed" : "Uploading"} />
      <span>{failed ? "Failed" : upload.attempts ? `Retry ${upload.attempts}/3` : "..."}</span>
      {failed ? (
        <>
          <button
            type="button"
            className={ClassName.retryPhotoButton}
            onClick={() => onRetry(upload.key)}
            title="Retry upload"
            aria-label="Retry photo upload"
          >
            <IoRefreshOutline size={17} />
          </button>
          <button
            type="button"
            className={ClassName.removeQueuedPhotoButton}
            onClick={() => onRemove(upload.key)}
            title="Delete failed photo"
            aria-label="Delete failed photo"
          >
            <IoClose size={17} />
          </button>
        </>
      ) : null}
    </div>
  );
}

function QuickCapturePage() {
  useTabActive(SideBarTabs.QuickCapture.name);
  const cameraInput = useRef<HTMLInputElement>(null);
  const galleryInput = useRef<HTMLInputElement>(null);
  const uploadChain = useRef<Promise<void>>(Promise.resolve());
  const activeUploadKeys = useRef(new Set<string>());
  const uploadPreviewUrls = useRef(new Map<string, string>());
  const retryTimers = useRef(new Map<string, number>());
  const enqueueUploadRef = useRef<(item: PendingCaptureUpload) => void>(() => undefined);
  const queueRestored = useRef(false);
  const [mode, setMode] = useState<ViewMode>("capture");
  const [dealers, setDealers] = useState<DealerType[]>([]);
  const [dealerSearch, setDealerSearch] = useState("");
  const [dealerName, setDealerName] = useState("");
  const [selectedDealer, setSelectedDealer] = useState<DealerType | null>(null);
  const [drafts, setDrafts] = useState<CarCaptureDraftType[]>([]);
  const [draftsLoaded, setDraftsLoaded] = useState(false);
  const [currentDraft, setCurrentDraft] = useState<CarCaptureDraftType | null>(null);
  const [carMakes, setCarMakes] = useState<CarMakeType[]>([]);
  const [price, setPrice] = useState("");
  const [usageType, setUsageType] = useState<"used" | "new">("new");
  const [note, setNote] = useState("");
  const [localUploads, setLocalUploads] = useState<LocalUpload[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [cropImage, setCropImage] = useState<CaptureDraftImageType | null>(null);
  const [cropOptions, setCropOptions] = useState({ zoom: 1, horizontal: 50, vertical: 50 });
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraDraftId, setCameraDraftId] = useState("");

  const loadDrafts = useCallback(async () => {
    const result = await getCaptureDrafts({ limit: 100 });
    setDrafts(result.drafts);
    setDraftsLoaded(true);
  }, []);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    try {
      const [dealerRows, makeRows] = await Promise.all([getDealers(""), getCarMakes()]);
      setDealers(dealerRows);
      setCarMakes(makeRows);
      await loadDrafts();
    } catch (error: any) {
      setMessage(error?.response?.data?.msg || "Quick Capture could not load.");
    } finally {
      setLoading(false);
    }
  }, [loadDrafts]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  const getUploadPreview = useCallback((item: PendingCaptureUpload) => {
    const existing = uploadPreviewUrls.current.get(item.key);
    if (existing) return existing;
    const preview = URL.createObjectURL(item.blob);
    uploadPreviewUrls.current.set(item.key, preview);
    return preview;
  }, []);

  const releaseUploadPreview = useCallback((key: string) => {
    const preview = uploadPreviewUrls.current.get(key);
    if (preview) URL.revokeObjectURL(preview);
    uploadPreviewUrls.current.delete(key);
  }, []);

  useEffect(
    () => () => {
      retryTimers.current.forEach((timer) => window.clearTimeout(timer));
      retryTimers.current.clear();
      uploadPreviewUrls.current.forEach((preview) => URL.revokeObjectURL(preview));
      uploadPreviewUrls.current.clear();
    },
    []
  );

  const uploadQueuedItem = useCallback(
    async (item: PendingCaptureUpload) => {
      const preview = getUploadPreview(item);
      const attempts = Number(item.attempts || 0);
      setLocalUploads((current) => [
        ...current.filter((upload) => upload.key !== item.key),
        {
          key: item.key,
          draftId: item.draftId,
          preview,
          status: "uploading",
          attempts,
          lastError: item.lastError || "",
        },
      ]);
      try {
        await uploadCaptureDraftImage(
          item.draftId,
          item.blob,
          item.clientId,
          item.fileName
        );
        await removePendingCaptureUpload(item.key);
        setLocalUploads((current) =>
          current.filter((upload) => upload.key !== item.key)
        );
        releaseUploadPreview(item.key);
        try {
          const refreshed = await getCaptureDraft(item.draftId);
          setCurrentDraft((current) =>
            current?._id === refreshed._id ? refreshed : current
          );
          setDrafts((current) => [
            refreshed,
            ...current.filter((draft) => draft._id !== refreshed._id),
          ]);
        } catch {
          // The photo is already stored. A later refresh will reconcile the draft.
        }
      } catch (error: any) {
        const uploadError = captureUploadError(error);
        const nextAttempts = attempts + 1;
        const destinationGone =
          uploadError.status === 404 ||
          (uploadError.status === 400 &&
            /not editable|published|archived/i.test(uploadError.message));

        if (destinationGone) {
          await removePendingCaptureUpload(item.key);
          setLocalUploads((current) =>
            current.filter((upload) => upload.key !== item.key)
          );
          releaseUploadPreview(item.key);
          setMessage("A queued photo was removed because its draft no longer exists.");
          return;
        }

        const terminalStatus = [400, 401, 403, 413].includes(uploadError.status);
        const canRetry = !terminalStatus && nextAttempts < MAX_UPLOAD_ATTEMPTS;
        const updated: PendingCaptureUpload = {
          ...item,
          attempts: nextAttempts,
          lastError: uploadError.message,
          status: canRetry ? "pending" : "failed",
        };
        await putPendingCaptureUpload(updated);
        setLocalUploads((current) =>
          current.map((upload) =>
            upload.key === item.key
              ? {
                  ...upload,
                  status: canRetry ? "uploading" : "failed",
                  attempts: nextAttempts,
                  lastError: uploadError.message,
                }
              : upload
          )
        );

        if (canRetry) {
          const existingTimer = retryTimers.current.get(item.key);
          if (existingTimer) window.clearTimeout(existingTimer);
          const timer = window.setTimeout(() => {
            retryTimers.current.delete(item.key);
            enqueueUploadRef.current(updated);
          }, RETRY_DELAYS[Math.min(nextAttempts - 1, RETRY_DELAYS.length - 1)]);
          retryTimers.current.set(item.key, timer);
        }
      }
    },
    [getUploadPreview, releaseUploadPreview]
  );

  const filteredDealers = useMemo(() => {
    const search = dealerSearch.trim().toLowerCase();
    if (!search) return dealers.slice(0, 8);
    return dealers
      .filter(
        (dealer) =>
          dealer.phoneNumber?.toLowerCase().includes(search) ||
          dealer.name?.toLowerCase().includes(search)
      )
      .slice(0, 8);
  }, [dealerSearch, dealers]);

  useEffect(() => {
    if (selectedDealer || dealerSearch.trim().length < 3) return;
    const timer = window.setTimeout(() => {
      getDealers(dealerSearch.trim())
        .then(setDealers)
        .catch(() => undefined);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [dealerSearch, selectedDealer]);

  const enqueueUpload = useCallback(
    (item: PendingCaptureUpload) => {
      if (activeUploadKeys.current.has(item.key)) return;
      activeUploadKeys.current.add(item.key);
      uploadChain.current = uploadChain.current
        .catch(() => undefined)
        .then(() => uploadQueuedItem(item))
        .finally(() => activeUploadKeys.current.delete(item.key));
    },
    [uploadQueuedItem]
  );

  useEffect(() => {
    enqueueUploadRef.current = enqueueUpload;
  }, [enqueueUpload]);

  useEffect(() => {
    if (!draftsLoaded || queueRestored.current) return;
    queueRestored.current = true;

    void getPendingCaptureUploads().then(async (uploads) => {
      const byDraft = new Map<string, PendingCaptureUpload[]>();
      uploads.forEach((upload) => {
        byDraft.set(upload.draftId, [
          ...(byDraft.get(upload.draftId) || []),
          upload,
        ]);
      });

      await Promise.all(
        Array.from(byDraft.entries()).map(async ([draftId, draftUploads]) => {
          try {
            const draft = await getCaptureDraft(draftId);
            if (draft.status !== "Draft") {
              await removePendingCaptureUploadsForDraft(draftId);
              return;
            }
            setDrafts((current) => [
              draft,
              ...current.filter((item) => item._id !== draft._id),
            ]);
            draftUploads.forEach((upload) => {
              const attempts = Number(upload.attempts || 0);
              if (upload.status === "failed" || attempts >= MAX_UPLOAD_ATTEMPTS) {
                const preview = getUploadPreview(upload);
                setLocalUploads((current) => [
                  ...current.filter((item) => item.key !== upload.key),
                  {
                    key: upload.key,
                    draftId: upload.draftId,
                    preview,
                    status: "failed",
                    attempts,
                    lastError: upload.lastError || "Photo upload failed",
                  },
                ]);
                return;
              }
              enqueueUpload(upload);
            });
          } catch (error: any) {
            if (Number(error?.response?.status || 0) === 404) {
              await removePendingCaptureUploadsForDraft(draftId);
            }
          }
        })
      );
    });
  }, [draftsLoaded, enqueueUpload, getUploadPreview]);

  const ensureDraft = async () => {
    if (currentDraft) return currentDraft;
    if (!selectedDealer) throw new Error("Select a dealer first");
    const draft = await createCaptureDraft({
      dealer: selectedDealer._id,
      clientDraftId: newClientId(),
      price: rawNumber(price),
      usageType,
      description: note,
    });
    setCurrentDraft(draft);
    setDrafts((current) => [draft, ...current]);
    return draft;
  };

  const queueCaptureBlob = async (draftId: string, blob: Blob) => {
    const clientId = newClientId();
    const item: PendingCaptureUpload = {
      key: `${draftId}:${clientId}`,
      draftId,
      clientId,
      blob,
      fileName: `${clientId}.jpg`,
      createdAt: Date.now(),
      attempts: 0,
      status: "pending",
      lastError: "",
    };
    await putPendingCaptureUpload(item);
    enqueueUpload(item);
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setMessage("");
    setLoading(true);
    try {
      const draft = await ensureDraft();
      for (const file of Array.from(files)) {
        const blob = await compressCaptureImage(file);
        await queueCaptureBlob(draft._id, blob);
      }
    } catch (error: any) {
      setMessage(error?.message || "Could not prepare the photos.");
    } finally {
      setLoading(false);
      if (cameraInput.current) cameraInput.current.value = "";
      if (galleryInput.current) galleryInput.current.value = "";
    }
  };

  const openContinuousCamera = async () => {
    if (!selectedDealer && !currentDraft) {
      setMessage("Select a dealer first.");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      cameraInput.current?.click();
      return;
    }

    setLoading(true);
    try {
      const draft = await ensureDraft();
      setCameraDraftId(draft._id);
      setCameraOpen(true);
    } catch (error: any) {
      setMessage(error?.message || "Could not start the camera.");
    } finally {
      setLoading(false);
    }
  };

  const closeContinuousCamera = () => {
    setCameraOpen(false);
    setCameraDraftId("");
  };

  const useFallbackCamera = () => {
    closeContinuousCamera();
    window.setTimeout(() => cameraInput.current?.click(), 0);
  };

  const currentDraftId = currentDraft?._id;
  useEffect(() => {
    if (!currentDraftId) return;
    const timer = window.setTimeout(() => {
      void updateCaptureDraft(currentDraftId, {
        price: rawNumber(price),
        usageType,
        description: note,
      });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [price, usageType, note, currentDraftId]);

  const retryUpload = async (key: string) => {
    const item = (await getPendingCaptureUploads()).find((upload) => upload.key === key);
    if (!item) return;
    const retryItem: PendingCaptureUpload = {
      ...item,
      attempts: 0,
      lastError: "",
      status: "pending",
    };
    await putPendingCaptureUpload(retryItem);
    setLocalUploads((current) =>
      current.map((upload) =>
        upload.key === key
          ? { ...upload, status: "uploading", attempts: 0, lastError: "" }
          : upload
      )
    );
    enqueueUpload(retryItem);
  };

  const removeQueuedUpload = async (key: string) => {
    const timer = retryTimers.current.get(key);
    if (timer) window.clearTimeout(timer);
    retryTimers.current.delete(key);
    await removePendingCaptureUpload(key);
    setLocalUploads((current) => current.filter((upload) => upload.key !== key));
    releaseUploadPreview(key);
  };

  const clearFailedUploads = async (draftId: string) => {
    const failedKeys = localUploads
      .filter((upload) => upload.draftId === draftId && upload.status === "failed")
      .map((upload) => upload.key);
    await Promise.all(failedKeys.map(removeQueuedUpload));
  };

  const clearQueuedUploadsForDraft = async (draftId: string) => {
    const keys = localUploads
      .filter((upload) => upload.draftId === draftId)
      .map((upload) => upload.key);
    keys.forEach((key) => {
      const timer = retryTimers.current.get(key);
      if (timer) window.clearTimeout(timer);
      retryTimers.current.delete(key);
      releaseUploadPreview(key);
    });
    await removePendingCaptureUploadsForDraft(draftId);
    setLocalUploads((current) =>
      current.filter((upload) => upload.draftId !== draftId)
    );
  };

  const saveAndNext = async () => {
    if (!currentDraft) {
      setMessage("Take at least one photo before saving this car.");
      return;
    }
    const updated = await updateCaptureDraft(currentDraft._id, {
      price: rawNumber(price),
      usageType,
      description: note,
    });
    setDrafts((current) => [updated, ...current.filter((item) => item._id !== updated._id)]);
    setCurrentDraft(null);
    setPrice("");
    setUsageType("new");
    setNote("");
    setMessage("Draft saved. Ready for the next car.");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const chooseDraft = (draft: CarCaptureDraftType) => {
    setCurrentDraft(draft);
    setPrice(formatPriceInput(draft.price));
    setUsageType(draft.usageType === "used" ? "used" : "new");
    setNote(draft.description || "");
    setMode(draft.status === "Draft" ? "edit" : "drafts");
    setMessage("");
  };

  const saveDraftPatch = async (patch: CaptureDraftPatch) => {
    if (!currentDraft) return;
    const updated = await updateCaptureDraft(currentDraft._id, patch);
    setCurrentDraft(updated);
    setPrice(formatPriceInput(updated.price));
    setDrafts((current) => [updated, ...current.filter((item) => item._id !== updated._id)]);
  };

  const updateImageLayout = async (
    images: CaptureDraftImageType[],
    selectedIds = images.filter((image) => image.selected).map((image) => image._id)
  ) => {
    await saveDraftPatch({
      imageOrder: images.map((image) => image._id),
      selectedImageIds: selectedIds,
    });
  };

  const moveImage = async (index: number, direction: -1 | 1) => {
    if (!currentDraft) return;
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= currentDraft.images.length) return;
    const images = [...currentDraft.images];
    [images[index], images[nextIndex]] = [images[nextIndex], images[index]];
    await updateImageLayout(images);
  };

  const toggleImage = async (image: CaptureDraftImageType) => {
    if (!currentDraft) return;
    const selected = currentDraft.images.filter((item) => item.selected).map((item) => item._id);
    const next = image.selected
      ? selected.filter((id) => id !== image._id)
      : selected.length < 10
      ? [...selected, image._id]
      : selected;
    if (!image.selected && selected.length >= 10) {
      setMessage("A public listing can use up to 10 photos.");
      return;
    }
    await updateImageLayout(currentDraft.images, next);
  };

  const removeImage = async (imageId: string) => {
    if (!currentDraft) return;
    await deleteCaptureDraftImage(currentDraft._id, imageId);
    const refreshed = await getCaptureDraft(currentDraft._id);
    setCurrentDraft(refreshed);
    await loadDrafts();
  };

  const applyCrop = async () => {
    if (!currentDraft || !cropImage) return;
    setLoading(true);
    try {
      const blob = await cropCaptureImage(cropImage.url, cropOptions);
      await replaceCaptureDraftImage(currentDraft._id, cropImage._id, blob);
      const refreshed = await getCaptureDraft(currentDraft._id);
      setCurrentDraft(refreshed);
      setCropImage(null);
      await loadDrafts();
    } catch (error: any) {
      setMessage(error?.message || "Could not crop this photo.");
    } finally {
      setLoading(false);
    }
  };

  const publish = async () => {
    if (!currentDraft) return;
    setLoading(true);
    try {
      const updated = await publishCaptureDraft(currentDraft._id);
      setCurrentDraft(updated);
      setMessage("Published to the app and webapp. Telegram is queued.");
      await loadDrafts();
      setMode("drafts");
    } catch (error: any) {
      setMessage(error?.response?.data?.msg || "Could not publish this draft.");
    } finally {
      setLoading(false);
    }
  };

  const addDealer = async () => {
    const phoneNumber = dealerSearch.replace(/\s/g, "");
    if (!phoneNumber) return;
    const dealer = await quickCreateDealer({ phoneNumber, name: dealerName });
    setDealers((current) => [dealer, ...current.filter((item) => item._id !== dealer._id)]);
    setSelectedDealer(dealer);
    setDealerSearch(dealer.phoneNumber);
    setDealerName("");
  };

  const activeLocalUploads = useMemo(
    () =>
      currentDraft
        ? localUploads.filter((upload) => upload.draftId === currentDraft._id)
        : [],
    [currentDraft, localUploads]
  );
  const activeFailedUploads = activeLocalUploads.filter(
    (upload) => upload.status === "failed"
  );
  const pendingUploadsByDraft = useMemo(() => {
    const counts = new Map<string, number>();
    localUploads.forEach((upload) => {
      counts.set(upload.draftId, (counts.get(upload.draftId) || 0) + 1);
    });
    return counts;
  }, [localUploads]);

  return (
    <div className={`${CommonStyles.pageWrapper} ${ClassName.page}`}>
      <Menu />
      <header className={ClassName.header}>
        <div>
          <p className={ClassName.eyebrow}>BOSSCAR OPERATIONS</p>
          <h1 className={ClassName.heading}>Quick car capture</h1>
        </div>
        <div className={ClassName.tabs}>
          <button className={mode === "capture" ? ClassName.activeTab : ClassName.tab} onClick={() => setMode("capture")}>
            Capture
          </button>
          <button className={mode !== "capture" ? ClassName.activeTab : ClassName.tab} onClick={() => { setMode("drafts"); setCurrentDraft(null); }}>
            Drafts <span className={ClassName.count}>{drafts.filter((draft) => draft.status === "Draft").length}</span>
          </button>
        </div>
      </header>

      {message && <div className={ClassName.message}>{message}</div>}
      <input ref={cameraInput} type="file" accept="image/*" capture="environment" className="hidden" onChange={(event) => void handleFiles(event.target.files)} />
      <input ref={galleryInput} type="file" accept="image/*" multiple className="hidden" onChange={(event) => void handleFiles(event.target.files)} />

      {mode === "capture" && (
        <main className={ClassName.captureGrid}>
          <section className={ClassName.panel}>
            <h2 className={ClassName.sectionTitle}>1. Dealer</h2>
            {selectedDealer ? (
              <div className={ClassName.selectedDealer}>
                <div>
                  <strong>{selectedDealer.name}</strong>
                  <p>{selectedDealer.phoneNumber}</p>
                </div>
                <button className={ClassName.iconButton} onClick={() => { setSelectedDealer(null); setDealerSearch(""); }} title="Change dealer">
                  <IoClose size={22} />
                </button>
              </div>
            ) : (
              <>
                <div className={ClassName.searchRow}>
                  <IoSearchOutline size={21} />
                  <input
                    value={dealerSearch}
                    onChange={(event) => setDealerSearch(event.target.value)}
                    placeholder="Dealer phone or name"
                    className={ClassName.cleanInput}
                    inputMode="tel"
                  />
                </div>
                <div className={ClassName.dealerResults}>
                  {filteredDealers.map((dealer) => (
                    <button key={dealer._id} className={ClassName.dealerResult} onClick={() => { setSelectedDealer(dealer); setDealerSearch(dealer.phoneNumber); }}>
                      <span>{dealer.name}</span><strong>{dealer.phoneNumber}</strong>
                    </button>
                  ))}
                </div>
                {dealerSearch.trim() && !filteredDealers.length ? (
                  <div className={ClassName.quickDealer}>
                    <input value={dealerName} onChange={(event) => setDealerName(event.target.value)} placeholder="Dealer name (optional)" className={ClassName.input} />
                    <button className={ClassName.secondaryButton} onClick={addDealer}><IoAddOutline /> Add this phone</button>
                  </div>
                ) : null}
              </>
            )}
          </section>

          <section className={ClassName.panel}>
            <h2 className={ClassName.sectionTitle}>2. Car & price</h2>
            <div className={ClassName.conditionRow}>
              <button
                type="button"
                className={usageType === "used" ? ClassName.activeChoice : ClassName.choice}
                onClick={() => setUsageType("used")}
              >
                Used
              </button>
              <button
                type="button"
                className={usageType === "new" ? ClassName.activeChoice : ClassName.choice}
                onClick={() => setUsageType("new")}
              >
                New
              </button>
            </div>
            <div className={ClassName.priceRow}>
              <span>Birr</span>
              <input
                value={price}
                onChange={(event) => setPrice(formatPriceInput(event.target.value))}
                placeholder="0"
                inputMode="numeric"
                className={ClassName.priceInput}
              />
            </div>
            <label className={ClassName.fieldLabel}>
              Extra note <span>(optional)</span>
              <textarea
                className={ClassName.noteInput}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Anything important about this car"
                rows={2}
              />
            </label>
            <p className={ClassName.helper}>The draft is still saved if the price is missing.</p>
          </section>

          <section className={`${ClassName.panel} ${ClassName.photosPanel}`}>
            <div className={ClassName.sectionHeader}>
              <div>
                <h2 className={ClassName.sectionTitle}>3. Photos</h2>
                <p className={ClassName.helper}>
                  {currentDraft?.images.length || 0} uploaded
                  {activeLocalUploads.length ? ` / ${activeLocalUploads.length} pending` : ""}
                  {" / unlimited draft photos"}
                </p>
              </div>
              <IoCloudDoneOutline size={25} className="text-green-600" />
            </div>
            <div className={ClassName.captureActions}>
              <button disabled={!selectedDealer} className={ClassName.cameraButton} onClick={() => void openContinuousCamera()}>
                <IoCameraOutline size={28} /> Take next photo
              </button>
              <button disabled={!selectedDealer} className={ClassName.galleryButton} onClick={() => galleryInput.current?.click()}>
                <IoImagesOutline size={24} /> Gallery
              </button>
            </div>
            <div className={ClassName.photoGrid}>
              {currentDraft?.images.map((image, index) => (
                <div className={ClassName.photoThumb} key={image._id}>
                  <img src={image.url} alt={`Car ${index + 1}`} />
                  <span>{index + 1}</span>
                  <button
                    type="button"
                    className={ClassName.removePhotoButton}
                    onClick={() => void removeImage(image._id)}
                    title="Remove photo"
                    aria-label={`Remove photo ${index + 1}`}
                  >
                    <IoClose size={18} />
                  </button>
                </div>
              ))}
              {activeLocalUploads.map((upload) => (
                <QueuedUploadTile
                  key={upload.key}
                  upload={upload}
                  onRetry={(key) => void retryUpload(key)}
                  onRemove={(key) => void removeQueuedUpload(key)}
                />
              ))}
            </div>
            {activeFailedUploads.length > 1 ? (
              <button
                type="button"
                className={ClassName.clearFailedButton}
                onClick={() => currentDraft && void clearFailedUploads(currentDraft._id)}
              >
                Remove failed uploads
              </button>
            ) : null}
          </section>

          <button className={ClassName.nextButton} disabled={!currentDraft || loading} onClick={saveAndNext}>
            Save & next car <IoArrowBack className="rotate-180" size={21} />
          </button>
        </main>
      )}

      {mode === "drafts" && (
        <main className={ClassName.draftsLayout}>
          <div className={ClassName.draftsHeader}>
            <div><h2 className={ClassName.sectionTitle}>Captured cars</h2><p className={ClassName.helper}>Newest activity first. All staff can finish these drafts.</p></div>
            <button className={ClassName.iconButton} onClick={() => void loadDrafts()} title="Refresh"><IoRefreshOutline size={22} /></button>
          </div>
          <div className={ClassName.draftList}>
            {drafts.map((draft) => {
              const missing = draftMissing(draft);
              return (
                <button key={draft._id} className={ClassName.draftRow} onClick={() => chooseDraft(draft)}>
                  {draft.images?.[0] ? <img src={draft.images[0].url} alt="" /> : <div className={ClassName.emptyPhoto}><IoCameraOutline /></div>}
                  <div className={ClassName.draftMain}>
                    <div className={ClassName.draftTitle}><strong>{draft.dealerNameSnapshot}</strong><span className={draft.status === "Published" ? ClassName.successBadge : ClassName.draftBadge}>{draft.status}</span></div>
                    <p>{draft.dealerPhoneSnapshot} · {draft.images.length} photos</p>
                    {pendingUploadsByDraft.get(draft._id) ? (
                      <small>{pendingUploadsByDraft.get(draft._id)} queued photo(s)</small>
                    ) : null}
                    <p>{draft.price ? `${Number(draft.price).toLocaleString()} Birr` : "Price missing"}</p>
                    {missing.length > 0 && draft.status === "Draft" ? <small>Needs {missing.slice(0, 4).join(", ")}</small> : null}
                    {draft.telegram?.status && draft.status === "Published" ? <small>Telegram: {draft.telegram.status}</small> : null}
                  </div>
                </button>
              );
            })}
          </div>
        </main>
      )}

      {mode === "edit" && currentDraft && (
        <DraftEditor
          draft={currentDraft}
          carMakes={carMakes}
          loading={loading}
          onBack={() => { setMode("drafts"); setCurrentDraft(null); }}
          onPatch={saveDraftPatch}
          onMoveImage={moveImage}
          onToggleImage={toggleImage}
          onRemoveImage={removeImage}
          onCrop={(image) => { setCropImage(image); setCropOptions({ zoom: 1, horizontal: 50, vertical: 50 }); }}
          onTakePhoto={() => void openContinuousCamera()}
          onAddPhotos={() => galleryInput.current?.click()}
          pendingUploads={activeLocalUploads}
          onRetryUpload={(key) => void retryUpload(key)}
          onRemoveQueuedUpload={(key) => void removeQueuedUpload(key)}
          onClearFailedUploads={() => void clearFailedUploads(currentDraft._id)}
          onPublish={publish}
          onDelete={async () => {
            const draftId = currentDraft._id;
            await deleteCaptureDraft(draftId);
            await clearQueuedUploadsForDraft(draftId);
            setCurrentDraft(null);
            setMode("drafts");
            await loadDrafts();
          }}
        />
      )}

      {mode === "drafts" && currentDraft?.status === "Published" && (
        <div className={ClassName.telegramBar}>
          <span>Telegram: <strong>{currentDraft.telegram?.status}</strong></span>
          {currentDraft.telegram?.status === "Failed" ? (
            <button onClick={async () => setCurrentDraft(await retryCaptureTelegram(currentDraft._id))}>Retry Telegram</button>
          ) : null}
        </div>
      )}

      {cropImage && (
        <div className={ClassName.modalBackdrop}>
          <div className={ClassName.cropModal}>
            <div className={ClassName.sectionHeader}><h2 className={ClassName.sectionTitle}>Crop photo</h2><button className={ClassName.iconButton} onClick={() => setCropImage(null)}><IoClose size={24} /></button></div>
            <div className={ClassName.cropPreview}>
              <img
                src={cropImage.url}
                alt="Crop preview"
                style={{
                  transform: `scale(${cropOptions.zoom})`,
                  objectPosition: `${cropOptions.horizontal}% ${cropOptions.vertical}%`,
                }}
              />
            </div>
            <label>Zoom <input type="range" min="1" max="3" step="0.05" value={cropOptions.zoom} onChange={(event) => setCropOptions((current) => ({ ...current, zoom: Number(event.target.value) }))} /></label>
            <label>Left / right <input type="range" min="0" max="100" value={cropOptions.horizontal} onChange={(event) => setCropOptions((current) => ({ ...current, horizontal: Number(event.target.value) }))} /></label>
            <label>Up / down <input type="range" min="0" max="100" value={cropOptions.vertical} onChange={(event) => setCropOptions((current) => ({ ...current, vertical: Number(event.target.value) }))} /></label>
            <button className={ClassName.primaryButton} onClick={applyCrop} disabled={loading}>Apply crop</button>
          </div>
        </div>
      )}

      {cameraOpen && cameraDraftId ? (
        <ContinuousCamera
          initialPhotoCount={
            (currentDraft?.images.length || 0) +
            localUploads.filter((upload) => upload.key.startsWith(`${cameraDraftId}:`)).length
          }
          onAccept={(blob) => queueCaptureBlob(cameraDraftId, blob)}
          onClose={closeContinuousCamera}
          onFallback={useFallbackCamera}
        />
      ) : null}
    </div>
  );
}

function DraftEditor({
  draft,
  carMakes,
  loading,
  onBack,
  onPatch,
  onMoveImage,
  onToggleImage,
  onRemoveImage,
  onCrop,
  onTakePhoto,
  onAddPhotos,
  pendingUploads,
  onRetryUpload,
  onRemoveQueuedUpload,
  onClearFailedUploads,
  onPublish,
  onDelete,
}: {
  draft: CarCaptureDraftType;
  carMakes: CarMakeType[];
  loading: boolean;
  onBack: () => void;
  onPatch: (patch: CaptureDraftPatch) => Promise<void>;
  onMoveImage: (index: number, direction: -1 | 1) => Promise<void>;
  onToggleImage: (image: CaptureDraftImageType) => Promise<void>;
  onRemoveImage: (id: string) => Promise<void>;
  onCrop: (image: CaptureDraftImageType) => void;
  onTakePhoto: () => void;
  onAddPhotos: () => void;
  pendingUploads: LocalUpload[];
  onRetryUpload: (key: string) => void;
  onRemoveQueuedUpload: (key: string) => void;
  onClearFailedUploads: () => void;
  onPublish: () => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [form, setForm] = useState({
    price: formatPriceInput(draft.price),
    make: makeId(draft.make),
    model: draft.model || "",
    year: String(draft.year || ""),
    color: draft.color || "",
    mileage: String(draft.mileage || ""),
    batteryRangeKm: String(draft.batteryRangeKm || ""),
    transmissionType: draft.transmissionType || "",
    fuelType: draft.fuelType || "",
    usageType: draft.usageType === "used" ? "used" : "new",
    listingType: draft.listingType || "Sale",
    location: draft.location || "Addis Ababa",
    description: draft.description || "",
  });
  const selectedCount = draft.images.filter((image) => image.selected).length;

  const save = () =>
    onPatch({
      ...form,
      price: rawNumber(form.price),
      year: form.year,
      mileage: form.fuelType === "Electric" ? "" : form.mileage,
      batteryRangeKm:
        form.fuelType === "Electric" ? form.batteryRangeKm : "",
    });

  return (
    <main className={ClassName.editor}>
      <div className={ClassName.editorHeader}>
        <button className={ClassName.iconButton} onClick={onBack}><IoArrowBack size={23} /></button>
        <div><h2 className={ClassName.sectionTitle}>Complete car details</h2><p className={ClassName.helper}>{draft.dealerNameSnapshot} · {draft.dealerPhoneSnapshot}</p></div>
      </div>
      <section className={ClassName.editorSection}>
        <div className={ClassName.formGrid}>
          <label>Car brand<select value={form.make} onChange={(event) => setForm({ ...form, make: event.target.value })} className={ClassName.input}><option value="">Select brand</option>{carMakes.map((make) => <option key={make._id} value={make._id}>{make.name}</option>)}</select></label>
          <label>Year<input className={ClassName.input} inputMode="numeric" value={form.year} onChange={(event) => setForm({ ...form, year: event.target.value.replace(/\D/g, "").slice(0, 4) })} /></label>
          <label>Model<input className={ClassName.input} value={form.model} onChange={(event) => setForm({ ...form, model: event.target.value })} /></label>
          <label>Price<input className={ClassName.input} inputMode="numeric" value={form.price} onChange={(event) => setForm({ ...form, price: formatPriceInput(event.target.value) })} /></label>
          <label>Fuel<select className={ClassName.input} value={form.fuelType} onChange={(event) => setForm({ ...form, fuelType: event.target.value, mileage: event.target.value === "Electric" ? "" : form.mileage, batteryRangeKm: event.target.value === "Electric" ? form.batteryRangeKm : "" })}><option value="">Select fuel</option><option>Gas</option><option>Diesel</option><option>Hybrid</option><option>Electric</option></select></label>
          <label>Transmission<select className={ClassName.input} value={form.transmissionType} onChange={(event) => setForm({ ...form, transmissionType: event.target.value })}><option value="">Select transmission</option><option>Automatic</option><option>Manual</option><option>Semi-Automatic</option></select></label>
          <label>Usage<select className={ClassName.input} value={form.usageType} onChange={(event) => setForm({ ...form, usageType: event.target.value, color: event.target.value === "new" ? form.color : "" })}><option value="used">Used</option><option value="new">New</option></select></label>
          {form.usageType === "new" ? <label>Color<select className={ClassName.input} value={form.color} onChange={(event) => setForm({ ...form, color: event.target.value })}><option value="">Select color</option>{CAR_COLORS.map((item) => <option key={item}>{item}</option>)}</select></label> : null}
          <label>Listing<select className={ClassName.input} value={form.listingType} onChange={(event) => setForm({ ...form, listingType: event.target.value })}><option>Sale</option><option>Rent</option></select></label>
          <label>Location<input className={ClassName.input} value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value })} /></label>
          {form.fuelType === "Electric" ? (
            <label>KM range per charge<input className={ClassName.input} inputMode="numeric" value={form.batteryRangeKm} onChange={(event) => setForm({ ...form, batteryRangeKm: event.target.value.replace(/\D/g, "") })} /></label>
          ) : (
            <label>Mileage<input className={ClassName.input} inputMode="numeric" value={form.mileage} onChange={(event) => setForm({ ...form, mileage: event.target.value.replace(/\D/g, "") })} /></label>
          )}
        </div>
        <label>Extra note / description<textarea className={ClassName.textarea} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
        <button className={ClassName.saveDetailsButton} onClick={save}>Save details</button>
      </section>

      <section className={ClassName.editorSection}>
        <div className={ClassName.sectionHeader}><div><h2 className={ClassName.sectionTitle}>Choose listing photos</h2><p className={ClassName.helper}>{selectedCount}/10 selected · minimum 3 · first selected photo is primary</p></div></div>
        <div className="flex gap-2 mb-4">
          <button className={ClassName.cameraButton} onClick={onTakePhoto}><IoCameraOutline size={22} /> Take photo</button>
          <button className={ClassName.galleryButton} onClick={onAddPhotos}><IoImagesOutline size={20} /> Add photos</button>
        </div>
        <div className={ClassName.editPhotoGrid}>
          {draft.images.map((image, index) => (
            <div key={image._id} className={`${ClassName.editPhoto} ${image.selected ? "ring-2 ring-primary" : "opacity-60"}`}>
              <img src={image.url} alt={`Car ${index + 1}`} />
              <button className={ClassName.selectPhoto} onClick={() => onToggleImage(image)} title="Use in listing"><IoCheckmarkCircle size={23} /></button>
              <div className={ClassName.photoTools}>
                <button disabled={index === 0} onClick={() => onMoveImage(index, -1)} title="Move earlier"><IoArrowUp /></button>
                <button disabled={index === draft.images.length - 1} onClick={() => onMoveImage(index, 1)} title="Move later"><IoArrowDown /></button>
                <button onClick={() => onCrop(image)} title="Crop"><IoCropOutline /></button>
                <button onClick={() => onRemoveImage(image._id)} title="Remove"><IoTrashOutline /></button>
              </div>
            </div>
          ))}
          {pendingUploads.map((upload) => (
            <QueuedUploadTile
              key={upload.key}
              upload={upload}
              onRetry={onRetryUpload}
              onRemove={onRemoveQueuedUpload}
            />
          ))}
        </div>
        {pendingUploads.filter((upload) => upload.status === "failed").length > 1 ? (
          <button type="button" className={ClassName.clearFailedButton} onClick={onClearFailedUploads}>
            Remove failed uploads
          </button>
        ) : null}
      </section>

      <div className={ClassName.publishBar}>
        <button className={ClassName.deleteButton} onClick={onDelete}>Delete draft</button>
        <button className={ClassName.publishButton} onClick={async () => { await save(); await onPublish(); }} disabled={loading || selectedCount < 3}>Publish app, web & Telegram</button>
      </div>
    </main>
  );
}

export default QuickCapturePage;

const ClassName = {
  page: "flex-1 min-w-0 min-h-screen bg-[#f5f6f8] text-primaryText pb-28 xl:pb-10",
  header: "sticky top-0 z-20 bg-white border-b px-4 sm:px-8 pt-12 xl:pt-6 pb-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4",
  eyebrow: "text-[11px] font-bold text-primary tracking-widest",
  heading: "text-2xl sm:text-3xl font-bold",
  tabs: "flex gap-2",
  tab: "h-10 px-5 rounded-md bg-gray-100 text-sm font-semibold",
  activeTab: "h-10 px-5 rounded-md bg-black text-white text-sm font-semibold",
  count: "ml-2 rounded-full bg-primary text-white px-2 py-0.5 text-xs",
  message: "mx-4 sm:mx-8 mt-4 rounded-md border border-primary/20 bg-red-50 px-4 py-3 text-sm text-primary",
  captureGrid: "p-4 sm:p-8 grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-6xl w-full mx-auto",
  panel: "bg-white border rounded-md p-4 sm:p-5",
  photosPanel: "lg:col-span-2",
  sectionTitle: "text-lg font-bold",
  sectionHeader: "flex items-center justify-between gap-4 mb-4",
  helper: "text-sm text-secondaryText mt-1",
  selectedDealer: "flex justify-between items-center border border-green-300 bg-green-50 rounded-md p-4",
  searchRow: "h-13 border rounded-md flex items-center gap-3 px-4 focus-within:border-primary",
  cleanInput: "flex-1 min-w-0 outline-none bg-transparent h-full",
  dealerResults: "flex flex-col divide-y mt-2",
  dealerResult: "flex justify-between text-left py-3 px-2 hover:bg-gray-50 text-sm",
  quickDealer: "mt-3 flex flex-col sm:flex-row gap-2",
  conditionRow: "mt-4 grid grid-cols-2 gap-2",
  choice: "h-11 rounded-md border bg-white font-semibold",
  activeChoice: "h-11 rounded-md bg-black text-white font-semibold",
  fieldLabel: "mt-4 block text-sm font-semibold [&>span]:font-normal [&>span]:text-gray-500",
  priceRow: "mt-4 h-16 border-2 border-black rounded-md flex items-center px-4 gap-3",
  priceInput: "flex-1 min-w-0 text-2xl font-bold outline-none",
  noteInput: "mt-2 w-full rounded-md border px-3 py-2.5 outline-none focus:border-black resize-none font-normal",
  captureActions: "grid grid-cols-[1fr_auto] gap-2 mb-4",
  cameraButton: "h-16 rounded-md bg-primary text-white font-bold flex items-center justify-center gap-2 disabled:opacity-40",
  galleryButton: "h-16 px-5 rounded-md bg-black text-white font-bold flex items-center justify-center gap-2 disabled:opacity-40",
  photoGrid: "grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-2",
  photoThumb: "relative aspect-[4/3] rounded-md overflow-hidden bg-gray-100 [&>img]:w-full [&>img]:h-full [&>img]:object-cover [&>span]:absolute [&>span]:bottom-1 [&>span]:left-1 [&>span]:bg-black/70 [&>span]:text-white [&>span]:rounded [&>span]:px-2 [&>span]:text-xs",
  removePhotoButton: "absolute top-1 right-1 h-8 w-8 rounded-full bg-black/75 text-white flex items-center justify-center shadow-sm hover:bg-primary focus:outline-none focus:ring-2 focus:ring-white",
  retryPhotoButton: "absolute top-1 left-1 h-8 w-8 rounded-full bg-white/95 text-primary flex items-center justify-center shadow-sm hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary",
  removeQueuedPhotoButton: "absolute top-1 right-1 h-8 w-8 rounded-full bg-black/80 text-white flex items-center justify-center shadow-sm hover:bg-primary focus:outline-none focus:ring-2 focus:ring-white",
  clearFailedButton: "mt-3 text-sm font-semibold text-primary underline underline-offset-4",
  nextButton: "lg:col-span-2 h-14 rounded-md bg-black text-white font-bold flex items-center justify-center gap-2 disabled:opacity-40",
  draftsLayout: "p-4 sm:p-8 max-w-5xl w-full mx-auto",
  draftsHeader: "flex items-center justify-between mb-5",
  draftList: "bg-white border rounded-md divide-y overflow-hidden",
  draftRow: "w-full p-3 sm:p-4 flex gap-4 text-left hover:bg-gray-50 [&>img]:w-28 [&>img]:h-24 [&>img]:rounded-md [&>img]:object-cover",
  emptyPhoto: "w-28 h-24 rounded-md bg-gray-100 flex items-center justify-center text-2xl",
  draftMain: "flex-1 min-w-0 text-sm [&>p]:text-secondaryText [&>small]:block [&>small]:text-primary [&>small]:mt-1",
  draftTitle: "flex flex-wrap items-center justify-between gap-2 mb-1",
  draftBadge: "px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs",
  successBadge: "px-2 py-1 bg-green-100 text-green-800 rounded text-xs",
  editor: "my-3 sm:my-6 mx-auto max-w-6xl w-[calc(100%_-_1.5rem)] bg-white rounded-3xl shadow-primaryShadow p-4 sm:p-6 space-y-5",
  editorHeader: "flex items-center gap-3",
  editorSection: "border-t border-borderPrimary pt-5",
  formGrid: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 [&>label]:text-sm [&>label]:font-semibold",
  input: "mt-2 h-11 w-full rounded-2xl border border-borderPrimary px-4 bg-white font-normal outline-none focus:border-primary",
  textarea: "mt-2 min-h-24 w-full rounded-2xl border border-borderPrimary p-4 outline-none focus:border-primary",
  saveDetailsButton: "mt-3 h-11 rounded-2xl bg-primary text-white px-5 font-semibold",
  editPhotoGrid: "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3",
  editPhoto: "relative rounded-md overflow-hidden bg-gray-100 [&>img]:w-full [&>img]:aspect-[4/3] [&>img]:object-cover",
  selectPhoto: "absolute top-2 left-2 text-white drop-shadow-md",
  photoTools: "grid grid-cols-4 bg-black text-white [&>button]:h-10 [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button:disabled]:opacity-30",
  publishBar: "sticky bottom-0 z-20 bg-white border rounded-md p-3 flex gap-3",
  publishButton: "flex-1 h-13 rounded-md bg-primary text-white font-bold disabled:opacity-40",
  deleteButton: "px-4 text-primary font-semibold",
  primaryButton: "h-12 px-6 rounded-md bg-primary text-white font-bold",
  secondaryButton: "h-12 px-5 rounded-md bg-gray-100 font-bold flex items-center justify-center gap-2",
  iconButton: "w-11 h-11 shrink-0 rounded-full bg-gray-100 flex items-center justify-center",
  countBadge: "rounded-full bg-gray-100 px-2 py-1 text-xs",
  telegramBar: "fixed bottom-4 right-4 z-30 bg-black text-white rounded-md px-4 py-3 flex items-center gap-4 [&>button]:text-primary",
  modalBackdrop: "fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4",
  cropModal: "bg-white rounded-md p-4 sm:p-6 w-full max-w-xl flex flex-col gap-4 [&>label]:flex [&>label]:flex-col [&>label]:gap-1",
  cropPreview: "aspect-[4/3] overflow-hidden rounded-md bg-black [&>img]:w-full [&>img]:h-full [&>img]:object-cover",
};
